import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';
import { toast } from 'sonner';

const AuthContext = createContext(null);

// Configure axios defaults
axios.defaults.withCredentials = true;

// ---------------------------------------------------------------------------
// Global 401 handler — when ANY API call (other than /auth/login itself)
// returns 401, the session has been invalidated server-side (another device
// signed in, idle timeout, etc.). Clear local state and redirect to /login
// immediately. Without this, the UI would keep showing stale data + only
// toast on the failed request.
// ---------------------------------------------------------------------------
let isHandlingSessionEnd = false;
function endSession(message) {
  if (isHandlingSessionEnd) return;
  isHandlingSessionEnd = true;
  try {
    localStorage.removeItem('access_token');
  } catch (e) { /* ignore */ }
  try { toast.error(message || 'Your session has ended. Please sign in again.'); } catch (e) {}
  // Redirect to /login. Use replace so the back button doesn't return to the
  // protected page after re-login.
  setTimeout(() => {
    const here = window.location.pathname || '';
    if (!here.startsWith('/login')) {
      window.location.replace('/login');
    } else {
      isHandlingSessionEnd = false;
    }
  }, 150);
}

axios.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    const url = (error?.config?.url || '');
    const detail = error?.response?.data?.detail || '';
    // Skip the login endpoint itself — a 401 there is just wrong creds.
    const isLoginCall =
      url.includes('/api/auth/login') ||
      url.includes('/api/auth/logout') ||
      url.includes('/api/auth/refresh');
    if (status === 401 && !isLoginCall) {
      // Only kick the user when we know we WERE authenticated. The me-check
      // at boot fires before there's a session and also returns 401 — we
      // shouldn't redirect on that one.
      const hadToken = !!localStorage.getItem('access_token');
      if (hadToken) {
        let msg = 'Your session has ended. Please sign in again.';
        if (typeof detail === 'string' && detail.toLowerCase().includes('another device')) {
          msg = 'You were signed out — your account was used to sign in on another device.';
        } else if (typeof detail === 'string' && detail.toLowerCase().includes('inactivity')) {
          msg = 'Signed out due to inactivity. Please sign in again.';
        } else if (typeof detail === 'string' && detail.toLowerCase().includes('expired')) {
          msg = 'Your session expired. Please sign in again.';
        }
        endSession(msg);
      }
    }
    return Promise.reject(error);
  }
);

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
        headers
      });
      setUser(data);
    } catch (error) {
      setUser(false);
      localStorage.removeItem('access_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (username, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { username, password },
        { withCredentials: true }
      );
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      // Block cashier users without an active cashier dashboard
      if (data.role === 'campus_agent' && data.cashier_linked === false) {
        localStorage.removeItem('access_token');
        return {
          success: false,
          error: 'No active Cashier Dashboard is linked to this account. Please contact the system administrator.'
        };
      }
      setUser(data);
      return { success: true, must_change_password: !!data.must_change_password };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API_URL}/api/auth/logout`, {}, { 
        withCredentials: true,
        headers
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('access_token');
      setUser(false);
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    checkAuth,
    refreshMe: checkAuth,
    isAuthenticated: !!user && user !== false,
    isSuperAdmin: user?.role === 'super_admin',
    isEntityAdmin: user?.role === 'entity_admin',
    isStaff: user?.role === 'staff',
    isAmbassador: user?.role === 'ambassador',
    mustChangePassword: !!(user && user.must_change_password)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
